/**
 * SIM Verifier sub-category label map (Phase 40, D-01).
 *
 * The picker discovers sub-category codes LIVE from GET /broadcasts/sim-verifier-subs
 * — the list is NEVER hardcoded (learned from the wallet-tag bug). This module only
 * provides an OPTIONAL cosmetic friendly-name layer: a hardcoded code→label map that
 * is intentionally EMPTY for now (fillable later without a DB or admin store) and a
 * raw-uppercased-code fallback for any code not in the map.
 *
 * So `gf` → "GF", `pvr` → "PVR" today; add `{ gf: 'Green Field' }` here later to
 * upgrade the label with zero other changes.
 */

/**
 * Hardcoded, LOWERCASE-keyed cosmetic labels. EMPTY for now (D-01) — every
 * discovered code renders via the raw-uppercased fallback until a friendly name
 * is added here.
 */
export const SV_SUB_LABELS: Record<string, string> = {};

/**
 * Resolve a discovered SV sub-category code to a display label.
 * Looks up the (case-insensitive) friendly name, else falls back to the raw
 * uppercased code (e.g. 'gf' → 'GF').
 */
export function simVerifierSubLabel(code: string): string {
  return SV_SUB_LABELS[code.toLowerCase()] ?? code.toUpperCase();
}

/**
 * Trivial hook wrapper so the screen consumes the label map as a hook
 * (`const { labelFor } = useSimVerifierSubLabels()`). No network, no DB, no state
 * — pure local lookup. Kept as a hook for a stable consumption pattern and to
 * leave room for a future data-backed label source without a call-site change.
 */
export function useSimVerifierSubLabels(): { labelFor: (code: string) => string } {
  return { labelFor: simVerifierSubLabel };
}
